import { useEffect, useId, useRef, useState } from 'react';
import { Wordmark } from '../../brand/Wordmark';
import ui from '../../components/ui.module.css';
import { shared } from '../content/shared';
import { useHydrated } from '../lib/useHydrated';
import s from '../site.module.css';

/**
 * Whether this browser holds a portal session. Reads Supabase's stored session key (sb-<ref>-auth-token) without
 * loading supabase-js, so the marketing bundle stays small. It only decides whether to show "Go to your portal".
 */
export function hasPortalSession(storage: Pick<Storage, 'length' | 'key'> | undefined = safeLocalStorage()): boolean {
  if (!storage) return false;
  for (let i = 0; i < storage.length; i++) if (/^sb-.+-auth-token$/.test(storage.key(i) ?? '')) return true;
  return false;
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** The public pages' header: lockup, the three links, the primary call to action. A disclosure menu at phone width. */
export function SiteHeader() {
  const h = shared.header;
  const [open, setOpen] = useState(false);
  // Read after hydration only, so the server HTML and the first client render agree.
  const signedIn = useHydrated() && hasPortalSession();
  const menuId = useId();
  const toggle = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggle.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className={s.header}>
      <div className={s.headerInner}>
        <a href="/" className={s.homeLink} aria-label={h.homeLabel}>
          <Wordmark size={26} />
        </a>
        <button
          ref={toggle}
          type="button"
          className={s.menuButton}
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => setOpen((o) => !o)}
        >
          <span className={s.menuIcon} aria-hidden="true" />
          {h.menuLabel}
        </button>
        <nav id={menuId} aria-label={h.navLabel} className={`${s.nav} ${open ? s.navOpen : ''}`}>
          <ul className={s.navList}>
            {h.links.map((l) => (
              <li key={l.href}>
                <a href={l.href} className={s.navLink}>
                  {l.label}
                </a>
              </li>
            ))}
            {signedIn && (
              <li>
                <a href={h.portal.href} className={s.navLink}>
                  {h.portal.label}
                </a>
              </li>
            )}
            <li>
              <a href={h.primary.href} className={`${ui.button} ${ui.primary} ${s.navCta}`}>
                {h.primary.label}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}
